import { useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Card } from '@components/card';
import { NumberFieldType } from '@components/form/v2';
import { HorizontalTabItem } from '@components/horizontal-tabs';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconSettings } from '@components/icons/settings';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { NumberField } from '@components/number-field';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { formatNumber, hasNumericValue } from '@utils/numbers';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import {
  clearStaticNumberDefaultIfInvalid,
  getBuilderDisplayDefault,
  getNumberRestrictions,
} from '../default-value-input';
import {
  clearMappedNumberDefaultIfInvalid,
  DefaultValueMapping,
  DefaultValueMappingDisplay,
} from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';
import { StaleConditionWarningIcon } from '../stale-condition-warning';

type Config = FieldConfig<'input-number'>;

const Display: Config['Display'] = ({ field }) => {
  const rawDisplayDefault = getBuilderDisplayDefault(field);
  const numericDisplayDefault = rawDisplayDefault != null ? Number(rawDisplayDefault) : null;
  const displayDefault =
    numericDisplayDefault != null && Number.isFinite(numericDisplayDefault)
      ? formatNumber(numericDisplayDefault)
      : undefined;
  return (
    <DefaultValueMappingDisplay field={field}>
      <TextField
        key={displayDefault ?? 'empty'}
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        placeholder={field.placeholder}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        labelIconAfter={<StaleConditionWarningIcon field={field} />}
        defaultValue={displayDefault}
      />
    </DefaultValueMappingDisplay>
  );
};

const FormBuilderNumber: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

export const numberFieldSwitchRow = (
  label: string,
  value: boolean,
  onChange: (value: boolean) => void,
  disabled?: boolean,
) => (
  <Stack space={1} orientation="horizontal" align="center" justify="space-between">
    <Text font="regular14" text={label} />
    <Switch
      label={label}
      labelVisible={false}
      value={value}
      onChange={onChange}
      isDisabled={disabled}
    />
  </Stack>
);

export const NumberFieldExampleCard = ({ isDecimal }: { isDecimal: boolean }) => {
  const number = isDecimal ? 1234.56 : 1234;
  return (
    <Card space={4} variant="gray">
      <Stack space={2} orientation="horizontal" align="center">
        {isDecimal ? (
          <IconDecimalFloat color="textSecondary" />
        ) : (
          <IconTextFormatNumber color="textSecondary" />
        )}
        <Text
          font="semibold12"
          color="textSecondary"
          text={`${t('example', { number: formatNumber(number) })}`}
        />
      </Stack>
    </Card>
  );
};

const NumberPropertiesTab = ({ field }: { field: NumberFieldType }) => {
  const {
    store: { actions },
  } = useFormBuilderContext();
  const [isDecimal, setIsDecimal] = useState(field.isDecimal);

  const minGreaterThanMax =
    field.restrictMin &&
    field.restrictMax &&
    hasNumericValue(field.min) &&
    hasNumericValue(field.max) &&
    Number(field.min) > Number(field.max);

  const minIsNegative =
    !field.allowNegative &&
    field.restrictMin &&
    hasNumericValue(field.min) &&
    Number(field.min) < 0;

  const maxIsNegative =
    !field.allowNegative &&
    field.restrictMax &&
    hasNumericValue(field.max) &&
    Number(field.max) < 0;

  const minError = minIsNegative
    ? t('errorNoNegativeMin')
    : minGreaterThanMax
      ? t('errorMinGreaterThanMax')
      : undefined;
  const maxError = maxIsNegative
    ? t('errorNoNegativeMax')
    : minGreaterThanMax
      ? t('errorMinGreaterThanMax')
      : undefined;

  const isEdit = Boolean(field.companyAttributeId || field.journeyAttributeId);
  const setMappedDefault = actions.createChangeHandler(field, 'mappedDefaultValue');
  const setStaticDefault = actions.createChangeHandler(field, 'defaultValue');

  const reconcileDefaults = (overrides: {
    restrictMin?: boolean;
    min?: number | null;
    restrictMax?: boolean;
    max?: number | null;
    allowNegative?: boolean;
    isDecimal?: boolean;
  }) => {
    const restrictMin = overrides.restrictMin ?? field.restrictMin;
    const min = overrides.min !== undefined ? overrides.min : field.min;
    const restrictMax = overrides.restrictMax ?? field.restrictMax;
    const max = overrides.max !== undefined ? overrides.max : field.max;
    const allowNegative = overrides.allowNegative ?? field.allowNegative;
    const isDecimalNext = overrides.isDecimal ?? field.isDecimal;
    const restrictions = getNumberRestrictions({
      restrictMin,
      min,
      restrictMax,
      max,
      allowNegative,
      isDecimal: isDecimalNext,
    });
    clearMappedNumberDefaultIfInvalid(field.mappedDefaultValue, restrictions, setMappedDefault);
    clearStaticNumberDefaultIfInvalid(field.defaultValue, restrictions, setStaticDefault);
  };

  return (
    <Content>
      <Stack space={4}>
        {numberFieldSwitchRow(
          t('allowDecimalNumbers'),
          field.isDecimal,
          (value) => {
            actions.createChangeHandler(field, 'isDecimal')(value);
            setIsDecimal(value);
            reconcileDefaults({ isDecimal: value });
          },
          isEdit,
        )}
        {numberFieldSwitchRow(t('allowNegativeNumbers'), field.allowNegative, (value) => {
          actions.createChangeHandler(field, 'allowNegative')(value);
          reconcileDefaults({ allowNegative: value });
        })}
        {numberFieldSwitchRow(t('minNumber'), field.restrictMin, (value) => {
          actions.createChangeHandler(field, 'restrictMin')(value);
          reconcileDefaults({ restrictMin: value });
        })}
        <Collapse isCollapsed={!field.restrictMin}>
          <Stack space={4}>
            <NumberField
              key={`min-number-${String(isDecimal)}`}
              onChange={(value) => {
                const next = value ?? null;
                actions.createChangeHandler(field, 'min')(next);
                reconcileDefaults({ min: next });
              }}
              defaultValue={field.min ?? undefined}
              label={t('minNumber')}
              hideLabel
              isDecimal={isDecimal}
              error={minError}
            />
          </Stack>
        </Collapse>
        {numberFieldSwitchRow(t('maxNumber'), field.restrictMax, (value) => {
          actions.createChangeHandler(field, 'restrictMax')(value);
          reconcileDefaults({ restrictMax: value });
        })}
        <Collapse isCollapsed={!field.restrictMax}>
          <Stack space={4}>
            <NumberField
              key={`max-number-${String(isDecimal)}`}
              onChange={(value) => {
                const next = value ?? null;
                actions.createChangeHandler(field, 'max')(next);
                reconcileDefaults({ max: next });
              }}
              defaultValue={field.max ?? undefined}
              label={t('maxNumber')}
              hideLabel
              isDecimal={isDecimal}
              error={maxError}
            />
          </Stack>
        </Collapse>
      </Stack>
      <NumberFieldExampleCard isDecimal={field.isDecimal} />
    </Content>
  );
};

const propertiesTab = (field: NumberFieldType): HorizontalTabItem => {
  return {
    label: t('properties'),
    icon: IconSettings,
    content: <NumberPropertiesTab field={field} />,
  };
};

const { t } = getScopedTranslate({
  en: {
    title: 'Number',
    label: 'Label',
    placeholder: 'Placeholder text',
    properties: 'Properties',
    minNumber: 'Min number',
    maxNumber: 'Max number',
    allowNegativeNumbers: 'Allow negative numbers',
    example: 'Example: {number}',
    allowDecimalNumbers: 'Allow decimal numbers',
    errorMinGreaterThanMax: 'Min cannot be greater than max',
    errorNoNegativeMin: 'You need to allow negative numbers in order to set a negative min number',
    errorNoNegativeMax: 'You need to allow negative numbers in order to set a negative max number',
  },
});

export const numberConfig: Config = {
  kind: 'input-number',
  title: t('title'),
  group: 'inputs',
  Icon: IconTextFormatNumber,
  propertiesTabs: [propertiesTab, conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Component: FormBuilderNumber,
};

const Content = styled(Stack).attrs({ space: 6 })({
  padding: pxToRem(24),
});
